import { inPracticeZone } from '@tebuto/functions/practice-time'
import { useComponentTranslation } from '@tebuto/functions/translation.components'
import { ComponentSpinner } from '@tebuto/layout/Spinner'
import { cn } from '@tebuto/lib/utils'
import ExtraSmallText from '@tebuto/typography/ExtraSmallText'
import MediumText from '@tebuto/typography/MediumText'
import SmallText from '@tebuto/typography/SmallText'
import { useCallback, useContext, useState } from 'react'
import { BookingContext } from '../context'
import { resolveEventLocation } from '../functions'
import { useParsedPrice } from '../hooks'
import { ParsedBookableEvent } from '../types'
import { BookingCalendarContext } from './context'
import LocationIndicator from './LocationIndicator'

function WidgetNoEntry({ text }: { text: string }) {
    return (
        <div
            data-testid="no-events-message"
            className={
                'text-center w-full py-4 px-2 border widget-border-primary-300 rounded-lg flex items-center justify-center gap-2 widget-bg-primary-50'
            }
        >
            <SmallText className={'font-medium widget-text-primary-color-500'}>{text}</SmallText>
        </div>
    )
}

export default function Events() {
    const { t } = useComponentTranslation()
    const { noEventsAvailable, noEventsForFilter, eventsForSelectedDate } = useContext(BookingCalendarContext)

    if (noEventsAvailable) {
        return <WidgetNoEntry text={t('booking.bookingCalendar.currentlyNoEventsAvailable')} />
    }

    if (noEventsForFilter) {
        return <WidgetNoEntry text={t('booking.bookingCalendar.noEventsForFilter')} />
    }

    if (!eventsForSelectedDate?.length) {
        return <WidgetNoEntry text={t('booking.bookingCalendar.noEventsOnDay')} />
    }

    return eventsForSelectedDate.map((event) => <Event key={bookableEventKey(event)} event={event} />)
}

// Flexible rules can offer multiple categories (and therefore durations) for the same rule at the
// same start time, so start/ruleId/therapist alone is not unique — categoryId and end must be included too.
export function bookableEventKey(event: ParsedBookableEvent): string {
    return `${event.start}-${event.end}-${event.ruleId}-${event.categoryId}-${event.therapist?.id ?? 'no-therapist'}`
}

function Event({ event }: { event: ParsedBookableEvent }) {
    const { t } = useComponentTranslation()
    const { previewMode, setBookingEvent, setNavigationDirection, setState, useCheckBookingAvailability } =
        useContext(BookingContext)
    const { categories, setIsEventNotAvailableDialogOpen, isMultiUser, availableCities } =
        useContext(BookingCalendarContext)
    const { checkBookingAvailability, isLoading: isCheckBookingAvailabilityLoading } = useCheckBookingAvailability()

    const [selected, setSelected] = useState(false)
    const category = categories[event.categoryId]
    const eventLocation = resolveEventLocation(event, categories)
    const isLoading = isCheckBookingAvailabilityLoading && selected
    const cityName = event.therapist?.address?.city?.name
    const showTherapistCity = availableCities.length > 1 && !!cityName
    const therapistLabel = showTherapistCity ? `${event.therapist?.name}, ${cityName}` : event.therapist?.name

    const { price, priceLabel } = useParsedPrice(category?.price ?? '0')

    const selectBookingEvent = useCallback(() => {
        if (previewMode) {
            setBookingEvent(event)
            setState('booking')
            setNavigationDirection('forward')
            return
        }

        setSelected(true)
        checkBookingAvailability({
            event,
            onSuccess: ({ isAvailable }) => {
                if (isAvailable) {
                    setBookingEvent(event)
                    setState('booking')
                    setNavigationDirection('forward')
                } else {
                    setIsEventNotAvailableDialogOpen(true)
                }
            }
        })
    }, [
        event,
        checkBookingAvailability,
        setBookingEvent,
        setState,
        setNavigationDirection,
        setIsEventNotAvailableDialogOpen,
        previewMode
    ])

    if (!category) return null

    return (
        <div
            onClick={selectBookingEvent}
            data-testid="booking-time-slot"
            className="relative w-full py-4 px-6 widget-bg-primary border shadow widget-border-primary-400 rounded-xl cursor-pointer transition-all duration-200 hover:shadow-md"
        >
            {isLoading && (
                <div className="absolute flex justify-center items-center w-full h-full top-0 left-0 bg-white/80 rounded-xl">
                    <ComponentSpinner />
                </div>
            )}
            <div className="absolute top-0 left-0 w-full h-full widget-bg-primary-50 opacity-50 rounded-xl z-0" />
            <div className={cn('w-full flex flex-col gap-1 z-10 relative', isLoading ? 'invisible' : '')}>
                <div className="flex justify-between items-center w-full flex-wrap">
                    <MediumText className="font-semibold widget-text-primary-color-600 leading-none!">
                        {inPracticeZone(event.startDatetime).toFormat('HH:mm')} Uhr
                    </MediumText>
                    <LocationIndicator location={eventLocation} />
                </div>
                <div className="flex justify-between items-center gap-2">
                    <ExtraSmallText className="widget-text-secondary text-sm leading-none!">
                        {category.name}, {event.endDatetime.diff(event.startDatetime).as('minutes')} Min.
                    </ExtraSmallText>
                    {price > 0 ? (
                        <ExtraSmallText className="widget-text-secondary leading-none!">
                            {priceLabel}
                            {t('euro')}
                        </ExtraSmallText>
                    ) : (
                        <ExtraSmallText className="widget-text-secondary leading-none!">{t('free')}</ExtraSmallText>
                    )}
                </div>
                {/* Show therapist name for multi-user setups */}
                {isMultiUser && event.therapist && (
                    <div className="flex items-center gap-1 mt-1">
                        <ExtraSmallText className="widget-text-secondary leading-none! opacity-75">
                            {t('booking.bookingCalendar.with')} {therapistLabel}
                        </ExtraSmallText>
                    </div>
                )}
            </div>
        </div>
    )
}
